import React, { useEffect, useState } from "react";
import { api, downloadApiFile, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { Field, PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RefreshCcw, FileSpreadsheet, ChevronLeft, ChevronRight } from "lucide-react";
import { SortableTh, useTableSort } from "@/components/SortableTh";
import DatePicker from "@/components/DatePicker";
import { fmtFechaHora } from "@/lib/fechas";
import { toast } from "sonner";

const I = String.fromCharCode(0xed);
const O = String.fromCharCode(0xf3);
const U = String.fromCharCode(0xfa);
const PAGE_SIZE = 50;
const DASH = "\u2014";
const LBL_EMPRESA = "C" + O + "digo empresa";

const AUDIT_SORT = {
  fecha: (a) => a.ts || "",
  empresa: (a) => a.codigo_empresa || a.empresa_id || "",
  entidad: (a) => a.entidad_label || a.entidad || "",
  accion: (a) => a.accion_label || a.accion || "",
  usuario: (a) => a.usuario_label || a.usuario_email || "",
  ip: (a) => a.ip || "",
  detalle: (a) => a.detalle_texto || "",
};

function paramsFrom(q) {
  const p = {};
  if (q.buscar) p.buscar = q.buscar;
  if (q.desde) p.desde = q.desde;
  if (q.hasta) p.hasta = q.hasta;
  if (q.accion) p.accion = q.accion;
  if (q.entidad) p.entidad = q.entidad;
  if (q.codigo_empresa) p.codigo_empresa = q.codigo_empresa;
  return p;
}

export default function AuditoriaPage() {
  const { user } = useAuth();
  const esAdmin = user?.rol === "admin";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [acciones, setAcciones] = useState({});
  const [entidades, setEntidades] = useState({});
  const [q, setQ] = useState({ buscar: "", desde: "", hasta: "", accion: "", entidad: "", codigo_empresa: "" });

  const load = async (pageN = page, filters = q) => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/v1/audit", {
        params: { ...paramsFrom(filters), page: pageN, page_size: PAGE_SIZE },
      });
      const totalN = data.total || 0;
      const last = Math.max(1, Math.ceil(totalN / PAGE_SIZE));
      if (pageN > last) {
        setPage(last);
        return;
      }
      setItems(data.items || []);
      setTotal(totalN);
      if (data.acciones) setAcciones(data.acciones);
      if (data.entidades) setEntidades(data.entidades);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => load(page, q), q.buscar ? 280 : 0);
    return () => clearTimeout(t);
  }, [page, q.buscar, q.desde, q.hasta, q.accion, q.entidad, q.codigo_empresa]); // eslint-disable-line

  const apply = (patch) => {
    setQ((prev) => ({ ...prev, ...patch }));
    setPage(1);
  };

  const exportXlsx = async () => {
    setExporting(true);
    try {
      await downloadApiFile("/api/v1/audit/export/xlsx", {
        params: paramsFrom(q),
        fallbackName: "auditoria_deca.xlsx",
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo descargar el archivo");
    } finally {
      setExporting(false);
    }
  };

  const { sorted, sort, toggle } = useTableSort(items, AUDIT_SORT);
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const hasMore = safePage < pageCount;

  return (
    <div>
      <PageHeader
        title={"Auditor" + I + "a"}
        subtitle={esAdmin
          ? "Eventos de acceso, generaci" + O + "n, modificaci" + O + "n, descarga y verificaci" + O + "n. Filtra por c" + O + "digo de empresa."
          : "Eventos de tu empresa: acceso, generaci" + O + "n, modificaci" + O + "n, descarga y verificaci" + O + "n."}
        testid="page-auditoria"
        actions={<>
          <Button variant="outline" onClick={exportXlsx} disabled={exporting} data-testid="btn-audit-xlsx">
            <FileSpreadsheet className="h-4 w-4 mr-2"/> {exporting ? "Descargando" + String.fromCharCode(0x2026) : "Excel"}
          </Button>
          <Button variant="outline" onClick={() => load()} disabled={loading}>
            <RefreshCcw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`}/> Actualizar
          </Button>
        </>}
      />

      <Card className="p-4 mb-4">
        <div className="flex flex-col lg:flex-row lg:flex-nowrap lg:items-end gap-2">
          <div className="min-w-0 flex-1">
            <Field label="Buscar">
              <Input
                value={q.buscar}
                onChange={(e) => apply({ buscar: e.target.value })}
                placeholder={"Usuario, email, IP, DeCA, empresa, acci" + O + "n"}
                data-testid="audit-buscar"
              />
            </Field>
          </div>
          <div className="w-[13.75rem] min-w-[13.75rem] shrink-0">
            <Field label="Desde">
              <DatePicker value={q.desde} onChange={(v) => apply({ desde: v })} testid="audit-desde"/>
            </Field>
          </div>
          <div className="w-[13.75rem] min-w-[13.75rem] shrink-0">
            <Field label="Hasta">
              <DatePicker value={q.hasta} onChange={(v) => apply({ hasta: v })} testid="audit-hasta"/>
            </Field>
          </div>
          <div className="w-full lg:w-[180px] shrink-0">
            <Label htmlFor="audit-accion" className="label-tag">{"Acci" + O + "n"}</Label>
            <Select value={q.accion || "all"} onValueChange={(v) => apply({ accion: v === "all" ? "" : v })}>
              <SelectTrigger id="audit-accion" data-testid="audit-accion"><SelectValue placeholder="Todas"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {Object.entries(acciones).map(([k, label]) => (
                  <SelectItem key={k} value={k}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {esAdmin ? (
            <div className="w-full lg:w-[140px] shrink-0">
              <Field label={LBL_EMPRESA}>
                <Input
                  value={q.codigo_empresa}
                  onChange={(e) => apply({ codigo_empresa: e.target.value })}
                  placeholder="DEMO2"
                  data-testid="audit-empresa"
                />
              </Field>
            </div>
          ) : null}
          <div className="w-full lg:w-[140px] shrink-0">
            <Label htmlFor="audit-entidad" className="label-tag">Entidad</Label>
            <Select value={q.entidad || "all"} onValueChange={(v) => apply({ entidad: v === "all" ? "" : v })}>
              <SelectTrigger id="audit-entidad" data-testid="audit-entidad"><SelectValue placeholder="Todas"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {Object.entries(entidades).map(([k, label]) => (
                  <SelectItem key={k} value={k}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="deca-table w-full min-w-[980px]">
            <thead><tr>
              <SortableTh label="Fecha" sortKey="fecha" sort={sort} onSort={toggle} />
              <SortableTh label="Empresa" sortKey="empresa" sort={sort} onSort={toggle} />
              <SortableTh label="Entidad" sortKey="entidad" sort={sort} onSort={toggle} />
              <SortableTh label={"Acci" + O + "n"} sortKey="accion" sort={sort} onSort={toggle} />
              <SortableTh label="Usuario" sortKey="usuario" sort={sort} onSort={toggle} />
              <SortableTh label="IP" sortKey="ip" sort={sort} onSort={toggle} />
              <SortableTh label="Detalle" sortKey="detalle" sort={sort} onSort={toggle} />
            </tr></thead>
            <tbody>
              {sorted.map((a) => (
                <tr key={a.id} data-testid={`audit-row-${a.id}`}>
                  <td className="font-mono text-xs whitespace-nowrap">{fmtFechaHora(a.ts)}</td>
                  <td className="font-mono text-xs">{a.codigo_empresa || a.empresa_id || DASH}</td>
                  <td>
                    <div className="font-medium">{a.entidad_label || a.entidad}</div>
                    {a.entidad_ref ? <div className="text-xs text-slate-500 font-mono">{a.entidad_ref}</div> : null}
                  </td>
                  <td><span className="status-badge status-borrador">{a.accion_label || a.accion}</span></td>
                  <td>
                    <div className="font-medium">{a.usuario_label || DASH}</div>
                    {a.usuario_email && a.usuario_email !== a.usuario_label ? (
                      <div className="text-xs text-slate-500">{a.usuario_email}</div>
                    ) : null}
                  </td>
                  <td className="font-mono text-xs">{a.ip || DASH}</td>
                  <td className="text-sm text-slate-700 max-w-[420px] whitespace-normal break-words">
                    {a.detalle_texto || DASH}
                  </td>
                </tr>
              ))}
              {items.length === 0 && !loading ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-slate-500">
                    {total === 0 && !q.buscar && !q.desde && !q.hasta && !q.accion && !q.entidad && !q.codigo_empresa
                      ? "Sin eventos a" + U + "n."
                      : "No hay eventos con esos filtros."}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 p-3 border-t border-slate-200 bg-slate-50" data-testid="audit-pagination">
          <div className="text-xs text-slate-500">
            {"P" + String.fromCharCode(0xe1) + "gina "}
            <span className="font-semibold text-slate-900">{safePage}</span>
            {total > 0 ? <> · {pageCount.toLocaleString("es-ES")} p{String.fromCharCode(0xe1)}ginas · {total.toLocaleString("es-ES")} registros</> : null}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(Math.max(1, safePage - 1))} disabled={safePage <= 1} data-testid="audit-prev">
              <ChevronLeft className="h-4 w-4 mr-1"/> Anterior
            </Button>
            <span className="text-xs font-mono text-slate-700 px-2">{"P" + String.fromCharCode(0xe1) + "g. "}{safePage}</span>
            <Button variant="outline" size="sm" onClick={() => setPage(safePage + 1)} disabled={!hasMore} data-testid="audit-next">
              Siguiente <ChevronRight className="h-4 w-4 ml-1"/>
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
